// src/components/coins/ClassGiveCoins.js
//
// The Classroom's "🎁 Give coins" panel — the clear version of GiveCoinsBar.
//
//   1. WHO     the whole class, or the students ticked in the list
//   2. HOW     "Each student gets N" — or "Share N coins" split evenly
//   3. FROM    the class coin bank (coins bought or earned by visiting — no
//              daily limit) or free teacher gifts (up to 20 a time and 50
//              per student a day)
//   4. WHY     a short note the student sees in their popup
//
// Server: giveClassCoins (functions/coins.js) — checks the class, the
// roster and the limits, and takes bank coins in a transaction.

import React, { useMemo, useState } from "react";
import { giveClassCoins, useWallet } from "../../services/coinService";
import BuyCoinsButton from "./BuyCoinsButton";
import { Coin } from "./CoinCounter";
import "./coins.css";

const EACH = [5, 10, 20, 50];
const REASONS = ["Great work!", "Super effort!", "Kind helper!", "Great listening!", "Awesome teamwork!", "Most improved!", "Perfect score!"];
const FREE_MAX = 20; // keep in step with functions/coins.js RULES.gift
const BANK_MAX = 500;
const CHUNK = 60;

const ClassGiveCoins = ({ students = [], selected = [], onClear, pickHint = "in the list below", startWho = "all" }) => {
  const wallet = useWallet();
  const bank = Number(wallet.classBank) || 0;
  const [who, setWho] = useState(startWho); // all | picked
  const [mode, setMode] = useState("each"); // each | share
  const [each, setEach] = useState(10);
  const [total, setTotal] = useState(100);
  const [fromBank, setFromBank] = useState(true);
  const [reason, setReason] = useState(REASONS[0]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const pickedOn = who === "picked";
  const list = pickedOn ? students.filter((s) => selected.includes(s.uid)) : students;
  const n = list.length;
  const perKid = mode === "each" ? Math.floor(Number(each) || 0) : n ? Math.floor((Number(total) || 0) / n) : 0;
  const cost = perKid * n;
  const max = fromBank ? BANK_MAX : FREE_MAX;

  const problem = useMemo(() => {
    if (!n) return pickedOn ? `Tick the students you want to give coins to (${pickHint}).` : "There are no students in your class yet.";
    if (perKid < 1) return mode === "share" ? `Share at least ${n} coins so everyone gets 1.` : "Give at least 1 coin.";
    if (perKid > max) return fromBank ? `Up to ${BANK_MAX} coins each at a time.` : `Free teacher gifts are up to ${FREE_MAX} coins each at a time — use the class coin bank for more.`;
    if (fromBank && cost > bank) return `That's ${cost.toLocaleString()} coins — your class coin bank has ${bank.toLocaleString()}.`;
    return "";
  }, [n, pickedOn, perKid, mode, max, fromBank, cost, bank, pickHint]);

  const give = async () => {
    if (problem) return;
    setBusy(true);
    setMsg(null);
    try {
      let given = 0;
      let capped = 0;
      let notIn = 0;
      let left = null;
      const uids = list.map((s) => s.uid);
      for (let i = 0; i < uids.length; i += CHUNK) {
        // eslint-disable-next-line no-await-in-loop
        const out = await giveClassCoins({ studentUids: uids.slice(i, i + CHUNK), amount: perKid, reason, fromBank });
        given += out.given || 0;
        capped += (out.capped || []).length;
        notIn += (out.notInClass || []).length;
        if (out.classBank !== undefined) left = out.classBank;
      }
      const parts = [`${given} student${given === 1 ? "" : "s"} got ${perKid} coin${perKid === 1 ? "" : "s"} 🎉`];
      if (capped) parts.push(fromBank ? `${capped} missed out — the bank ran out` : `${capped} already had today's 50 free teacher coins`);
      if (notIn) parts.push(`${notIn} ${notIn === 1 ? "isn't" : "aren't"} on your class list`);
      if (left !== null) parts.push(`${left.toLocaleString()} left in the bank`);
      setMsg({ tone: given ? "ok" : "warn", text: `${parts.join(" · ")}.` });
      if (given && pickedOn) onClear?.();
    } catch (err) {
      setMsg({ tone: "bad", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sbl-cgc" aria-label="Give coins">
      <header className="sbl-cgc-head">
        <h3>
          <span aria-hidden="true">🎁</span> Give coins
        </h3>
        <span className="sbl-cgc-bank">
          <Coin size={18} /> Class coin bank <b>{bank.toLocaleString()}</b>
        </span>
        <BuyCoinsButton educator compact label="Buy more" />
      </header>

      <div className="sbl-cgc-steps">
        <div className="sbl-cgc-step">
          <span className="sbl-cgc-num">1</span>
          <div>
            <p className="sbl-cgc-q">Who?</p>
            <div className="sbl-cgc-seg">
              <button type="button" className={who === "all" ? "is-on" : ""} onClick={() => setWho("all")}>
                Whole class ({students.length})
              </button>
              <button type="button" className={who === "picked" ? "is-on" : ""} onClick={() => setWho("picked")}>
                Ticked students ({selected.length})
              </button>
            </div>
            {pickedOn && <p className="sbl-cgc-tip">Tick ☑ students {pickHint} — one student or as many as you like.</p>}
          </div>
        </div>

        <div className="sbl-cgc-step">
          <span className="sbl-cgc-num">2</span>
          <div>
            <p className="sbl-cgc-q">How many?</p>
            <div className="sbl-cgc-seg">
              <button type="button" className={mode === "each" ? "is-on" : ""} onClick={() => setMode("each")}>
                Same for each
              </button>
              <button type="button" className={mode === "share" ? "is-on" : ""} onClick={() => setMode("share")}>
                Share a total
              </button>
            </div>
            {mode === "each" ? (
              <div className="sbl-cgc-amounts">
                {EACH.map((a) => (
                  <button key={a} type="button" className={Number(each) === a ? "is-on" : ""} onClick={() => setEach(a)}>
                    {a}
                  </button>
                ))}
                <label>
                  or <input type="number" min="1" max={max} value={each} onChange={(e) => setEach(e.target.value)} aria-label="Coins for each student" /> each
                </label>
              </div>
            ) : (
              <div className="sbl-cgc-amounts">
                <label>
                  Share <input type="number" min="1" value={total} onChange={(e) => setTotal(e.target.value)} aria-label="Coins to share" /> coins
                </label>
                <span className="sbl-cgc-tip">{n ? `= ${perKid} each for ${n} student${n === 1 ? "" : "s"}` : ""}</span>
              </div>
            )}
          </div>
        </div>

        <div className="sbl-cgc-step">
          <span className="sbl-cgc-num">3</span>
          <div>
            <p className="sbl-cgc-q">From where?</p>
            <div className="sbl-cgc-seg">
              <button type="button" className={fromBank ? "is-on" : ""} onClick={() => setFromBank(true)}>
                Class coin bank
              </button>
              <button type="button" className={!fromBank ? "is-on" : ""} onClick={() => setFromBank(false)}>
                Free teacher gift
              </button>
            </div>
            <p className="sbl-cgc-tip">{fromBank ? "No daily limit — comes out of your bank." : `Free: up to ${FREE_MAX} each at a time, 50 per student a day.`}</p>
          </div>
        </div>

        <div className="sbl-cgc-step">
          <span className="sbl-cgc-num">4</span>
          <div>
            <p className="sbl-cgc-q">Why? (they'll see this)</p>
            <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Why">
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="sbl-cgc-go">
        {problem ? (
          <p className="sbl-cgc-problem">{problem}</p>
        ) : (
          <p className="sbl-cgc-sum">
            {n} student{n === 1 ? "" : "s"} × <b>{perKid}</b> = <Coin size={16} /> <b>{cost.toLocaleString()}</b> coins
          </p>
        )}
        <button type="button" className="sbl-cgc-btn" onClick={give} disabled={busy || Boolean(problem)}>
          {busy ? "Giving…" : "Give coins"}
        </button>
      </div>
      {msg && <p className={`sbl-give-msg is-${msg.tone}`}>{msg.text}</p>}
    </section>
  );
};

export default ClassGiveCoins;
